import { useState, useRef, useEffect, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { MapPin } from 'lucide-react'

interface PlaceSuggestion {
  placeId: string
  description: string
}

interface PlaceAutocompleteResponse {
  suggestions: PlaceSuggestion[]
}

interface PlaceAutocompleteProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  id?: string
  className?: string
}

function useSessionToken() {
  const [token, setToken] = useState(() => crypto.randomUUID())
  const reset = useCallback(() => {
    setToken(crypto.randomUUID())
  }, [])
  return { token, reset }
}

export function PlaceAutocomplete({
  value,
  onChange,
  placeholder = 'City, Country',
  id,
  className,
}: PlaceAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value)
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const { token: sessionToken, reset: resetSession } = useSessionToken()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Sync external value changes
  useEffect(() => {
    setInputValue(value)
  }, [value])

  const [debouncedQuery, setDebouncedQuery] = useState('')

  useEffect(() => {
    if (inputValue.length < 2) {
      setDebouncedQuery('')
      return
    }
    const timer = setTimeout(() => setDebouncedQuery(inputValue), 300)
    return () => clearTimeout(timer)
  }, [inputValue])

  const { data } = useQuery({
    queryKey: ['places-autocomplete', debouncedQuery, sessionToken],
    queryFn: () =>
      api<PlaceAutocompleteResponse>(
        `/api/places/autocomplete?q=${encodeURIComponent(debouncedQuery)}&sessionToken=${sessionToken}`,
      ),
    enabled: debouncedQuery.length >= 2 && isOpen,
    staleTime: 60_000,
  })

  const suggestions = data?.suggestions || []

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  function selectSuggestion(suggestion: PlaceSuggestion) {
    setInputValue(suggestion.description)
    onChange(suggestion.description)
    setIsOpen(false)
    setHighlightedIndex(-1)
    resetSession()
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value
    setInputValue(val)
    setIsOpen(true)
    setHighlightedIndex(-1)
    // If user clears or modifies, update parent with raw text
    onChange(val)
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!isOpen || suggestions.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((i) => (i < suggestions.length - 1 ? i + 1 : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((i) => (i > 0 ? i - 1 : suggestions.length - 1))
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault()
      selectSuggestion(suggestions[highlightedIndex])
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={className}
          autoComplete="off"
        />
      </div>

      {isOpen && suggestions.length > 0 && (
        <ul className="bg-surface border-outline-variant/30 absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md border shadow-lg">
          {suggestions.map((s, i) => (
            <li
              key={s.placeId}
              onMouseDown={() => selectSuggestion(s)}
              onMouseEnter={() => setHighlightedIndex(i)}
              className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-sm ${
                i === highlightedIndex
                  ? 'bg-surface-container-highest'
                  : 'hover:bg-surface-container'
              }`}
            >
              <MapPin className="text-on-surface-variant size-4 shrink-0" />
              <span>{s.description}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
